/**
 * Local Storage Service
 * Handles all localStorage operations
 * Single Responsibility: Local storage abstraction
 */

const SMS_CONSENT_KEY = "checkoutSmsConsent";
const CUSTOMER_INFO_KEY = "checkoutCustomerInfo";
const DINING_OPTION_KEY = "checkoutDiningOption";
const ORDER_CONFIG_KEY = "checkoutOrderConfig";
const DELIVERY_ADDRESS_KEY = "checkoutDeliveryAddress";
const LAST_USED_ALIAS_KEY = "lastUsedAlias";
const SELECTED_LOCATION_KEY_PREFIX = "selectedLocation_";
const IDEMPOTENCY_KEY = "checkoutIdempotencyKey";

/**
 * Generates a UUID for use as a checkout idempotency key.
 * Falls back to a timestamp+random string where crypto.randomUUID is missing.
 * @returns {string}
 */
function generateUuid() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch (e) {
    // fall through to manual generation
  }
  return `${Date.now().toString(16)}-${Math.random()
    .toString(16)
    .slice(2)}-${Math.random()
    .toString(16)
    .slice(2)}`;
}

/**
 * Returns the current checkout idempotency key, creating and persisting one if
 * none exists. The same key is reused across retries of a single checkout
 * attempt (including after a page refresh) so the backend can dedupe them.
 * @returns {string}
 */
export function getOrCreateIdempotencyKey() {
  try {
    let key = localStorage.getItem(IDEMPOTENCY_KEY);
    if (!key) {
      key = generateUuid();
      localStorage.setItem(IDEMPOTENCY_KEY, key);
    }
    return key;
  } catch (e) {
    // localStorage unavailable (private mode): fall back to an ephemeral key.
    return generateUuid();
  }
}

/**
 * Returns the stored idempotency key without creating one, or null.
 * @returns {string|null}
 */
export function peekIdempotencyKey() {
  try {
    return localStorage.getItem(IDEMPOTENCY_KEY);
  } catch (e) {
    return null;
  }
}

/**
 * Clears the idempotency key. Call after an order concludes (success or an
 * explicit decline) so the next order starts a fresh attempt.
 */
export function clearIdempotencyKey() {
  try {
    localStorage.removeItem(IDEMPOTENCY_KEY);
  } catch (e) {
    // ignore
  }
}

/**
 * Saves SMS consent preference
 * @param {boolean} consent - SMS consent value
 */
export function saveSmsConsent(consent) {
  try {
    localStorage.setItem(SMS_CONSENT_KEY, JSON.stringify(consent));
  } catch (e) {
    console.warn("Failed to save SMS consent to localStorage:", e);
  }
}

/**
 * Loads SMS consent preference
 * @returns {boolean} SMS consent value (default: false)
 */
export function loadSmsConsent() {
  try {
    const saved = localStorage.getItem(SMS_CONSENT_KEY);
    return saved ? JSON.parse(saved) : false;
  } catch (e) {
    console.warn("Failed to load SMS consent from localStorage:", e);
    return false;
  }
}

/**
 * Saves last used store alias
 * @param {string} alias - Store alias
 */
export function saveLastUsedAlias(alias) {
  try {
    localStorage.setItem(LAST_USED_ALIAS_KEY, alias);
  } catch (e) {
    console.warn("Failed to save last used alias to localStorage:", e);
  }
}

/**
 * Loads last used store alias
 * @returns {string|null} Last used alias or null
 */
export function loadLastUsedAlias() {
  try {
    return localStorage.getItem(LAST_USED_ALIAS_KEY);
  } catch (e) {
    console.warn("Failed to load last used alias from localStorage:", e);
    return null;
  }
}

/**
 * Saves selected location for a store
 * @param {string} storeAlias - Store alias
 * @param {Object} location - Location object
 */
export function saveSelectedLocation(storeAlias, location) {
  try {
    const key = `${SELECTED_LOCATION_KEY_PREFIX}${storeAlias}`;
    localStorage.setItem(key, JSON.stringify(location));
  } catch (e) {
    console.warn("Failed to save selected location to localStorage:", e);
  }
}

/**
 * Loads selected location for a store
 * @param {string} storeAlias - Store alias
 * @returns {Object|null} Location object or null
 */
export function loadSelectedLocation(storeAlias) {
  try {
    const key = `${SELECTED_LOCATION_KEY_PREFIX}${storeAlias}`;
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : null;
  } catch (e) {
    console.warn("Failed to load selected location from localStorage:", e);
    return null;
  }
}

/**
 * Removes selected location for a store
 * @param {string} storeAlias - Store alias
 */
export function removeSelectedLocation(storeAlias) {
  try {
    const key = `${SELECTED_LOCATION_KEY_PREFIX}${storeAlias}`;
    localStorage.removeItem(key);
  } catch (e) {
    console.warn("Failed to remove selected location from localStorage:", e);
  }
}

/**
 * Saves customer info to localStorage
 * @param {Object} customerInfo - Customer info object with firstName, lastName, email, phone
 */
export function saveCustomerInfo(customerInfo) {
  try {
    const infoToSave = {
      first_name: customerInfo.firstName || customerInfo.first_name || null,
      last_name: customerInfo.lastName || customerInfo.last_name || null,
      email: customerInfo.email || null,
      phone: customerInfo.phone || null
    };
    localStorage.setItem(CUSTOMER_INFO_KEY, JSON.stringify(infoToSave));
  } catch (e) {
    console.warn("Failed to save customer info to localStorage:", e);
  }
}

/**
 * Loads customer info from localStorage
 * @returns {Object|null} Customer info object or null
 */
export function loadCustomerInfo() {
  try {
    const saved = localStorage.getItem(CUSTOMER_INFO_KEY);
    if (saved) {
      const customerInfo = JSON.parse(saved);
      return {
        firstName: customerInfo.first_name || customerInfo.firstName || "",
        lastName: customerInfo.last_name || customerInfo.lastName || "",
        email: customerInfo.email || "",
        phone: customerInfo.phone || ""
      };
    }
    return null;
  } catch (e) {
    console.warn("Failed to load customer info from localStorage:", e);
    return null;
  }
}

/**
 * Saves dining option selection to localStorage
 * @param {number} diningOptionId - Dining option ID
 */
export function saveDiningOption(diningOptionId) {
  try {
    localStorage.setItem(DINING_OPTION_KEY, JSON.stringify(diningOptionId));
  } catch (e) {
    console.warn("Failed to save dining option to localStorage:", e);
  }
}

/**
 * Loads dining option selection from localStorage
 * @returns {number|null} Dining option ID or null
 */
export function loadDiningOption() {
  try {
    const saved = localStorage.getItem(DINING_OPTION_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (e) {
    console.warn("Failed to load dining option from localStorage:", e);
    return null;
  }
}

/**
 * Saves order config to localStorage
 * @param {Object} orderConfig - Order config object with type, timing, scheduledDay, scheduledTime
 */
export function saveOrderConfig(orderConfig) {
  try {
    const configToSave = {
      type: orderConfig.type || "pickup",
      timing: orderConfig.timing || "asap",
      scheduledDay: orderConfig.scheduledDay || null,
      scheduledTime: orderConfig.scheduledTime || null
    };
    localStorage.setItem(ORDER_CONFIG_KEY, JSON.stringify(configToSave));
  } catch (e) {
    console.warn("Failed to save order config to localStorage:", e);
  }
}

/**
 * Loads order config from localStorage
 * @returns {Object|null} Order config object or null
 */
export function loadOrderConfig() {
  try {
    const saved = localStorage.getItem(ORDER_CONFIG_KEY);
    if (saved) {
      const config = JSON.parse(saved);
      return {
        type: config.type || "pickup",
        timing: config.timing || "asap",
        scheduledDay: config.scheduledDay || null,
        scheduledTime: config.scheduledTime || null
      };
    }
    return null;
  } catch (e) {
    console.warn("Failed to load order config from localStorage:", e);
    return null;
  }
}

/**
 * Saves delivery address to localStorage
 * @param {Object} address - Delivery address object with addressLine1, addressLine2, city, state, zip
 */
export function saveDeliveryAddress(address) {
  try {
    const addressToSave = {
      addressLine1: address.addressLine1 || address.address_1 || "",
      addressLine2: address.addressLine2 || address.address_2 || "",
      city: address.city || "",
      state: address.state || "",
      zip: address.zip || address.postal_code || ""
    };
    localStorage.setItem(DELIVERY_ADDRESS_KEY, JSON.stringify(addressToSave));
  } catch (e) {
    console.warn("Failed to save delivery address to localStorage:", e);
  }
}

/**
 * Loads delivery address from localStorage
 * @returns {Object|null} Delivery address object or null
 */
export function loadDeliveryAddress() {
  try {
    const saved = localStorage.getItem(DELIVERY_ADDRESS_KEY);
    if (saved) {
      const address = JSON.parse(saved);
      return {
        addressLine1: address.addressLine1 || "",
        addressLine2: address.addressLine2 || "",
        city: address.city || "",
        state: address.state || "",
        zip: address.zip || ""
      };
    }
    return null;
  } catch (e) {
    console.warn("Failed to load delivery address from localStorage:", e);
    return null;
  }
}
